import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { 
  Building, 
  Stethoscope, 
  Cpu, 
  Factory, 
  GraduationCap, 
  ShoppingCart,
  Plane,
  Shield,
  ArrowRight,
  CheckCircle
} from 'lucide-react'

export function Industries() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Industry Solutions - RFP Marketplace",
    "description": "Specialized RFP marketplace solutions for healthcare, technology, manufacturing, education, and more industries",
    "mainEntity": [
      {
        "@type": "Service",
        "name": "Healthcare RFP Solutions",
        "description": "Specialized procurement solutions for healthcare organizations"
      },
      {
        "@type": "Service", 
        "name": "Technology RFP Solutions",
        "description": "IT and software procurement marketplace for technology companies"
      }
    ]
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Industry-Specific RFP Solutions | Healthcare, Tech, Manufacturing & More"
        description="Specialized RFP marketplace solutions tailored for healthcare, technology, manufacturing, education, and other industries. Find qualified vendors for your sector."
        keywords="industry RFP solutions, healthcare procurement, technology RFP, manufacturing vendors, education RFP, sector-specific marketplace"
        canonical="https://sourcedin.ai/industries"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Industry-Specific 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> RFP Solutions</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Tailored procurement solutions for every industry. Connect with specialized vendors 
            who understand your unique requirements and compliance needs.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Specialized Solutions for Every Sector
            </h2>
            <p className="text-xl text-gray-600">
              Industry expertise meets procurement efficiency
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Healthcare */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow group cursor-pointer">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-red-100 group-hover:bg-red-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <Stethoscope className="h-8 w-8 text-red-600" />
                </div>
                <CardTitle className="text-2xl">Healthcare</CardTitle>
                <CardDescription className="text-base">
                  Medical equipment, pharmaceuticals, healthcare IT
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">FDA compliance tracking</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">HIPAA-compliant vendors</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Medical device specialists</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  Explore Healthcare <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Technology */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow group cursor-pointer">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-blue-100 group-hover:bg-blue-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <Cpu className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Technology</CardTitle>
                <CardDescription className="text-base">
                  Software development, IT services, cloud solutions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Certified tech partners</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Security-cleared vendors</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Agile development experts</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  Explore Technology <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Manufacturing */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow group cursor-pointer">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-orange-100 group-hover:bg-orange-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <Factory className="h-8 w-8 text-orange-600" />
                </div>
                <CardTitle className="text-2xl">Manufacturing</CardTitle>
                <CardDescription className="text-base">
                  Industrial equipment, materials, supply chain
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">ISO certified suppliers</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Quality assurance tracking</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Global supply network</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  Explore Manufacturing <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Education */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow group cursor-pointer">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-green-100 group-hover:bg-green-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl">Education</CardTitle>
                <CardDescription className="text-base">
                  EdTech, facilities, educational services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">FERPA compliant vendors</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Educational technology specialists</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Student-focused solutions</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  Explore Education <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Retail */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow group cursor-pointer">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-purple-100 group-hover:bg-purple-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingCart className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-2xl">Retail & E-commerce</CardTitle>
                <CardDescription className="text-base">
                  Logistics, POS systems, customer experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Omnichannel solutions</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">PCI DSS compliance</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Customer analytics experts</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  Explore Retail <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Government */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow group cursor-pointer">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-indigo-100 group-hover:bg-indigo-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-indigo-600" />
                </div>
                <CardTitle className="text-2xl">Government</CardTitle>
                <CardDescription className="text-base">
                  Public sector, defense, municipal services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Security clearance verified</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">FISMA compliance</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Public sector expertise</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  Explore Government <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industry Stats */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Trusted Across Industries
            </h2>
            <p className="text-xl text-gray-600">
              See how different sectors benefit from our specialized approach
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-gray-600">Healthcare Organizations</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">1.2K+</div>
              <div className="text-gray-600">Technology Companies</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">800+</div>
              <div className="text-gray-600">Manufacturing Firms</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">300+</div>
              <div className="text-gray-600">Educational Institutions</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Industry Specialization Matters */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Industry Specialization Matters
            </h2>
            <p className="text-xl text-gray-600">
              Generic solutions don't work for specialized industries
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Compliance Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Vendors pre-qualified for industry-specific regulations like HIPAA, 
                  SOX, FISMA, and other compliance requirements.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl">Domain Knowledge</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Connect with vendors who understand your industry's unique challenges, 
                  terminology, and business processes.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plane className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-2xl">Faster Implementation</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Reduce onboarding time and project risks with vendors who have 
                  proven experience in your specific industry.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Find Your Industry Experts
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Connect with specialized vendors who understand your unique industry requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                Start Industry Search <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-blue-600">
                Browse by Industry
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
